/**
 * SoilWorkspaceLayout — the single "Soil" workspace (U1).
 *
 * Owns programme resolution (?program= wins, else the tenant's active programme)
 * and the tab bar, and hands the resolved programme to each tab through the
 * router outlet context. Tabs are real routes, so per-menu RBAC gating is
 * unchanged: a tab renders only when its own menu is held.
 *
 * The error branch returns BEFORE the loading guard — a failed load must render
 * a notice, not sit on "loading" forever (the S1b/S2 brick-on-load lesson).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { Notice, TabBar, TabPanel } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useMenu } from '../../auth/useMenu';
import { soilProgramService, SoilProgram } from '../../services/soilProgramService';
import './nutrition.css';

export interface SoilWorkspaceContext {
  program: SoilProgram | null;
  programs: SoilProgram[];
  reload: () => void;
}

type TabKey = 'board' | 'sign-off' | 'setup';
const PATH_BY_TAB: Record<TabKey, string> = {
  board: '/nutrition/soil',
  'sign-off': '/nutrition/soil/sign-off',
  setup: '/nutrition/soil/setup',
};

const SoilWorkspaceLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get('program');

  const [programs, setPrograms] = useState<SoilProgram[] | null>(null);
  const [program, setProgram] = useState<SoilProgram | null>(null);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);

  const hasSessions = useMenu('fertilizing:sessions');
  const hasSignoff = useMenu('fertilizing:signoff');
  const hasProgramme = useMenu('fertilizing:programme');

  // Root 'Citrus Synch' crumb matches the existing convention in
  // NutritionBackCaptureListPage.tsx — every trail in the app starts there.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    { label: t('nutrition.soil.workspace.title') },
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await soilProgramService.list();
        if (cancelled) return;
        setPrograms(list);
        const requested = requestedId ? list.find((p) => p._id === requestedId) : undefined;
        // Only fall back to the active-programme lookup when no valid ?program= was given.
        setProgram(requested ?? (requestedId ? null : await soilProgramService.getActive()));
        setError('');
      } catch (_err) {
        if (!cancelled) { setPrograms([]); setError(t('nutrition.soil.workspace.loadFailed')); }
      }
    })();
    return () => { cancelled = true; };
    // `t` is deliberately absent: react-i18next's t is not referentially stable and
    // would re-fire this effect on every render (the S2 month-picker trap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedId, nonce]);

  // True only when a specific program was asked for, it did not resolve, AND
  // there is a list to choose from — the copy points at the selector, which
  // is itself gated on `programs.length > 0` below, so this must stay a
  // conjunction of all three. With no programs at all, an unresolvable id
  // falls through to the existing "no program" copy: there is no selector
  // to point at, so "create one" remains the right advice.
  const requestedMissing = Boolean(requestedId) && !program && (programs?.length ?? 0) > 0;

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const context: SoilWorkspaceContext = useMemo(
    () => ({ program, programs: programs || [], reload }),
    [program, programs, reload],
  );

  const activeTab: TabKey = location.pathname.startsWith('/nutrition/soil/sign-off')
    ? 'sign-off'
    : location.pathname.startsWith('/nutrition/soil/setup') ? 'setup' : 'board';

  const onSelectTab = (key: string) => {
    const to = PATH_BY_TAB[key as TabKey];
    // Carry the chosen programme across tabs so switching back to the board
    // does not silently reset it to the active one.
    navigate(program ? `${to}?program=${program._id}` : to);
  };

  const onSelectProgram = (id: string) => {
    setSearchParams(id ? { program: id } : {});
  };

  if (error) {
    return (
      <div className="pat-page" data-testid="soil-workspace-error">
        <PageHeader title={t('nutrition.soil.workspace.title')} />
        <Notice tone="danger">{error}</Notice>
      </div>
    );
  }
  if (programs === null) return <div className="pat-page">{t('common.loading')}</div>;

  return (
    <div className="pat-page" data-testid="soil-workspace">
      <PageHeader
        title={t('nutrition.soil.workspace.title')}
        meta={program
          ? `${program.season}${program.source?.vendor ? ` · ${program.source.vendor}` : ''}`
          : t('nutrition.soil.workspace.noProgram')}
        actions={activeTab === 'board' && programs.length > 0 ? (
          <select
            className="ntr-resolve-select"
            data-testid="soil-program-select"
            aria-label={t('nutrition.soil.workspace.programLabel')}
            value={program?._id || ''}
            onChange={(e) => onSelectProgram(e.target.value)}
          >
            <option value="">{t('nutrition.workspace.selectProgramme')}</option>
            {programs.map((p) => (
              <option key={p._id} value={p._id}>
                {p.season}{p.source?.vendor ? ` · ${p.source.vendor}` : ''}
              </option>
            ))}
          </select>
        ) : undefined}
      />

      <TabBar activeKey={activeTab} onSelect={onSelectTab} ariaLabel={t('nutrition.soil.workspace.title')}>
        {hasSessions ? (
          <TabPanel id="board" title={t('nutrition.soil.workspace.tab.board')}>
            {activeTab === 'board' ? <Outlet context={context} /> : null}
          </TabPanel>
        ) : null}
        {hasSignoff ? (
          <TabPanel id="sign-off" title={t('nutrition.soil.workspace.tab.signOff')}>
            {activeTab === 'sign-off' ? <Outlet context={context} /> : null}
          </TabPanel>
        ) : null}
        {hasProgramme ? (
          <TabPanel id="setup" title={t('nutrition.soil.workspace.tab.setup')}>
            {activeTab === 'setup' ? <Outlet context={context} /> : null}
          </TabPanel>
        ) : null}
      </TabBar>

      {!program && activeTab === 'board' && (
        <div className="ntr-empty" data-testid="soil-workspace-empty">
          <div className="ntr-empty__message">
            {requestedMissing ? t('nutrition.workspace.programmeNotFound') : t('nutrition.soil.workspace.emptyMessage')}
          </div>
          {!requestedMissing && hasProgramme && (
            <button type="button" className="pat-btn pat-btn--primary" onClick={() => navigate('/nutrition/soil/setup')}>
              {t('nutrition.soil.workspace.goToSetup')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SoilWorkspaceLayout;
